// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type {
  Callbacks,
  EventFunction,
  EventPayload,
  FactorOption,
  OrgPreferences,
} from '@imprivata-cloud/authn';
import {
  AppError,
  AuthnMethod,
  ErrorCode as AuthnErrorCode,
  EventType,
  FatalError as AuthnFatalError,
  getAttemptedUsername,
  ResetAuthError,
  RetryableError,
  toLoggableError,
} from '@imprivata-cloud/authn';
import { TERMINATE_REASON_CODE } from '@imprivata-cloud/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { SubscribeAuthnDataReadyFn } from '@imprivata-cloud/authn/lib/types/app';
import { option } from 'fp-ts';
import type { NOTIF_BANNER_TYPE } from '@imprivata-cloud/components';
import { useTranslation } from 'react-i18next';
import type { CredentialPrompt, RootState } from '../store/types';
import type { FaceSettingsGetResponse } from '../shared/types/api';
import type { BannerProps } from './auth/Banner/banner';
import { CredentialPromptKind, DirectPromptCause } from '../store/types';
import {
  endJourneyAndCloseWindow,
  getFactorTypes,
  showWindow,
  subscribeToAuthnDataReady,
  subscribeToFactorChanged,
} from '../agent-api';

import { ShowWindowOption } from '../agent-api/types';
import { getOrgPreferences } from '../api/api';
import { getFaceSettings } from '../api/appAccessWebService';
import { QueryParams } from '../api/constants';
import { mapErrorToAction } from '../api/hooks';
import { applyInterceptors } from '../api/interceptors';
import { ErrorCode } from '../api/new/types';
import getConfig from '../appConfigUtils';
import {
  AgentEvent,
  AuthenticationReadinessStatusCode,
  ERROR_BANNER_AUTO_CLOSE_MS,
  InternetConnectivityStatus,
  NETWORK_CONNECTION_STATUS_DEBOUNCE_TIMEOUT_SEC,
} from '../constants';
import { LearnCredentials as LearnCredentialsScreen } from '../containers';
import {
  useBootstrap,
  useDebounce,
  useGetEpicOauthFlowError,
  useRequestConfig,
} from '../hooks';
import {
  clearError,
  promptDirectCredentialsAction,
  resetAuthnDataAction,
  saveAgentSaml,
  saveAuthnDataAction,
  setAuthenticationStatusAction,
  startApplicationAccessAction,
} from '../store/actions';
import {
  type FactorDataReady,
  type FactorDataReadyEventHandler,
} from '../types';
import { getBuildInfo } from '../utils/build';
import {
  getCredentialsType,
  isDesktopAccess,
  isEpcsOrderSigning,
  isEpicOauthFlow,
  normalizeFactors,
  useNavigatorOnlineStatus,
  HARDCODED_FACTORS,
} from '../utils/utils';
import { Auth } from './auth';
import { PortalLayout } from './auth/portal-layout';
import { useBanner } from './auth/hooks/useBanner';
import { debug, log } from '../utils/logging';
import { getTraceContext } from '../tracing/traceContext';

let notified = false;

const offlinePrompt: CredentialPrompt = {
  kind: CredentialPromptKind.DIRECT,
  cause: DirectPromptCause.OFFLINE,
};

applyInterceptors();

const toAppError = (
  data: AppError | string,
  fallbackCode: AuthnErrorCode = AuthnErrorCode.UNKNOWN,
): AppError => {
  return typeof data === 'string'
    ? new AppError({ message: data, code: fallbackCode })
    : data;
};

// authn reads these when its face screen mounts; a param already in the URL wins
const FACE_PARAMS = [
  [QueryParams.SHOW_VIDEO, 'faceAuthVideoStreamEnabled'],
  [QueryParams.USER_CAN_SHOW_LIVE_FEED, 'userCanShowLiveFeed'],
] as const satisfies ReadonlyArray<
  readonly [string, keyof FaceSettingsGetResponse]
>;

const missingFaceParams = (search: URLSearchParams) =>
  FACE_PARAMS.filter(([param]) => !search.has(param));

const writeFaceParams = (settings: FaceSettingsGetResponse) => {
  const url = new URL(window.location.href);
  missingFaceParams(url.searchParams).forEach(([param, setting]) => {
    url.searchParams.set(param, String(settings[setting]));
  });
  window.history.replaceState(window.history.state, '', url);
};

// `run` keys <Auth>; advancing it remounts authn with a fresh session store
type AuthnState =
  | { kind: 'running'; run: number }
  | { kind: 'failed'; run: number; error: AppError; recoverable: boolean };

// faults a fresh authn session cannot clear: they need an admin or the user. Any other fatal
// error recovers, since authn lets a 5xx body's own code (e.g. implementation-defect) replace system-error.
const PERMANENT_FATAL_CODES: ReadonlySet<AuthnErrorCode> = new Set([
  AuthnErrorCode.FACTORS_INSUFFICIENT,
  AuthnErrorCode.INLINE_ENROLL_BLOCKED,
  AuthnErrorCode.UNSUPPORTED_INLINE_ENROLLMENT,
  AuthnErrorCode.FACTOR_LOCK_PERSISTENT,
  AuthnErrorCode.FACTOR_LOCK_TEMPORARY,
  AuthnErrorCode.FACTOR_LOCKED_NO_FALLBACK,
  AuthnErrorCode.ACCOUNT_LOCKED,
  AuthnErrorCode.INVALID_TENANT_ID,
]);
const AUTHN_FATAL_RECOVERY_MS = 15_000;

const failAuthn = (authnState: AuthnState, error: AppError): AuthnState => ({
  kind: 'failed',
  run: authnState.run,
  error,
  recoverable:
    error.code === undefined || !PERMANENT_FATAL_CODES.has(error.code),
});

const restartAuthn = (authnState: AuthnState): AuthnState => ({
  kind: 'running',
  run: authnState.run + 1,
});

// TODO: VN-20971 Add tests for this component
export const App = (): JSX.Element => {
  if (!notified) {
    const APP_VERSION = getBuildInfo();
    debug('APP VERSION', '[authn]:', APP_VERSION);
    notified = true;
  }

  const [orgPreferences, setOrgPreferences] = useState<
    OrgPreferences | null | undefined
  >(undefined);
  const [initialFactors, setInitialFactors] = useState<
    AuthnMethod[] | undefined
  >();
  const [offlineFactors, setOfflineFactors] = useState<
    FactorOption[] | undefined
  >();
  const [authnNetworkFailed, setAuthnNetworkFailed] = useState(false);
  // the agent's tap subscription outlives renders, so it reads the state from the ref
  const authnStateRef = useRef<AuthnState>({ kind: 'running', run: 0 });
  const [authnState, setAuthnState] = useState(authnStateRef.current);
  // every transition cancels the pending recovery, so only the current failure can restart authn
  const recoveryTimer = useRef<number>();
  const transitionAuthn = useCallback(
    (step: (current: AuthnState) => AuthnState) => {
      window.clearTimeout(recoveryTimer.current);
      const next = step(authnStateRef.current);
      authnStateRef.current = next;
      setAuthnState(next);
      if (next.kind === 'failed' && next.recoverable) {
        recoveryTimer.current = window.setTimeout(() => {
          authnStateRef.current = restartAuthn(authnStateRef.current);
          setAuthnState(authnStateRef.current);
        }, AUTHN_FATAL_RECOVERY_MS);
      }
    },
    [],
  );
  // authn brackets only the face enroll flow with START_ENROLLING; its ENROLLED carries no factor
  const enrollingFace = useRef(false);
  const { banner: authnBanner, setBanner, displayBanner } = useBanner();
  const clearBanner = useCallback(() => {
    setBanner(undefined);
  }, [setBanner]);

  const { t } = useTranslation();

  const showOfflineBanner = useCallback(() => {
    setBanner({
      status: 'warning',
      message: t('errors.offline'),
      subMessage: t('errors.offline-sub-message') || undefined,
    });
  }, [setBanner, t]);

  const { error, uiCaptureCredentials, networkConnectionStatus } = useSelector(
    (state: RootState) => ({
      error: state.error,
      uiCaptureCredentials: state.uiCaptureCredentials,
      networkConnectionStatus: state.networkConnectionStatus,
    }),
  );

  const config = getConfig();
  const dispatch = useDispatch();

  const store = useStore<RootState>();

  const [isOnline, setIsOnline] = useState<boolean | undefined>(undefined);
  const isNavigatorOnline = useNavigatorOnlineStatus();
  const debouncedNetworkConnectionStatus = useDebounce(
    networkConnectionStatus,
    NETWORK_CONNECTION_STATUS_DEBOUNCE_TIMEOUT_SEC * 1000,
  );

  useEffect(() => {
    log(
      `Network connection status: ${debouncedNetworkConnectionStatus}, isOnline: ${isOnline}, isNavigatorOnline: ${isNavigatorOnline}`,
    );
    if (
      debouncedNetworkConnectionStatus === InternetConnectivityStatus.CONNECTED
    ) {
      if (isOnline === false) {
        // this check added since CEF for some reason still if offline when EA sends CONNECTED status
        isNavigatorOnline && window.location.reload();
      } else {
        setIsOnline(true);
      }
    } else {
      setIsOnline(false);
    }
  }, [debouncedNetworkConnectionStatus, isOnline, isNavigatorOnline]);

  useEffect(() => {
    const tenantId = new URL(window.location.href).searchParams.get('tenantId');
    if (!tenantId) {
      return;
    }
    // a failed face lookup writes nothing, so authn keeps the preview hidden
    const faceSettings = missingFaceParams(
      new URLSearchParams(window.location.search),
    ).length
      ? getFaceSettings().catch(() => undefined)
      : undefined;
    // the params must be in the URL before orgPreferences lets <Auth> mount
    void Promise.all([
      getOrgPreferences().catch(() => null),
      faceSettings,
    ]).then(([preferences, settings]) => {
      if (settings) {
        writeFaceParams(settings);
      }
      setOrgPreferences(preferences);
    });
  }, []);

  useEffect(() => {
    getFactorTypes()
      .then(f => {
        const factors = normalizeFactors(f);
        debug('getFactorTypes', factors);
        setInitialFactors(factors);
      })
      .catch(err => {
        console.error('[AppAccess] getFactorTypes error', err);
        setInitialFactors(HARDCODED_FACTORS);
      });
  }, []);

  const errorBanner = useMemo<BannerProps | undefined>(() => {
    if (!error) {
      return undefined;
    }

    return {
      status: error.status ?? 'error',
      message: error.message ?? '',
      subMessage: error.subMessage,
      customIcon: error.customIcon,
    };
  }, [error]);

  useEffect(() => {
    if (!error) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      dispatch(clearError());
    }, ERROR_BANNER_AUTO_CLOSE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, error]);

  const sendAuthnDataReady = useCallback(
    (
      callback: FactorDataReadyEventHandler,
      eventName: AgentEvent,
      data: FactorDataReady,
    ) => {
      dispatch(saveAgentSaml(data.authnData.samlData));
      callback(eventName, data);
    },
    [dispatch],
  );

  const onAuthnDataReady = useCallback(
    (callback: SubscribeAuthnDataReadyFn) => {
      debug('subscribeToAuthnDataReady');
      const traceContext = getTraceContext();
      const authnData = store.getState().authnData;
      if (authnData && option.isSome(authnData)) {
        debug('sendAuthnDataReady');
        sendAuthnDataReady(callback, AgentEvent.FACTOR_DATA_READY, {
          traceContext,
          authnData: authnData.value,
        });
        dispatch(resetAuthnDataAction());
      }
      // authn subscribes once per mount; this subscription belongs to the run that mounted it
      const { run } = authnStateRef.current;
      subscribeToAuthnDataReady((event, data) => {
        const current = authnStateRef.current;
        if (current.kind === 'running' && current.run === run) {
          sendAuthnDataReady(callback, event, data);
          return;
        }
        // an unmounted authn must never see a tap: it would start a ghost session
        if (current.kind === 'failed' && !current.recoverable) {
          return;
        }
        // the next authn mount replays the buffered tap above
        dispatch(saveAuthnDataAction(data.authnData));
        if (current.kind === 'failed') {
          transitionAuthn(restartAuthn);
        }
      });
    },
    [store, sendAuthnDataReady, dispatch, transitionAuthn],
  );

  const authnCallbacks: Callbacks = useMemo(
    () => ({
      getFactorTypes,
      subscribeToFactorChanged,
      subscribeToAuthnDataReady: onAuthnDataReady,
    }),
    [onAuthnDataReady],
  );

  const contextResource = useMemo(() => {
    return {
      contextType:
        new URL(window.location.href).searchParams.get('contextType') || '',
      resourceType:
        new URL(window.location.href).searchParams.get('resourceType') || '',
    };
  }, []);

  const requestConfig = useRequestConfig();
  const { ready } = useBootstrap();

  const handleTapOver: EventFunction = useCallback(
    ({ event, data }: EventPayload) => {
      // in case tap-over the window should be shown only if user attention is required or failure occurred
      if (isDesktopAccess()) {
        switch (event) {
          case EventType.SUCCESS:
            if (data && data.nextFactor) {
              void showWindow(ShowWindowOption.show);
            }
            break;
          case EventType.NOT_READY:
          case EventType.AUTHENTICATED:
          case EventType.FACTORS:
          case EventType.READY:
          case EventType.INITIALIZED:
            break;
          case EventType.ERROR:
            // the offline screen shows the window once it mounts
            if (data?.code !== AuthnErrorCode.NETWORK_ERROR) {
              void showWindow(ShowWindowOption.show);
            }
            break;
          default: {
            void showWindow(ShowWindowOption.show);
          }
        }
      }
    },
    [],
  );

  const onEvent = useCallback(
    ({ event, data }: EventPayload) => {
      // the user-not-found error carries the typed username (PII); the agent copies console output into its log
      debug(
        'onEvent',
        JSON.stringify({
          event,
          data: data instanceof AppError ? toLoggableError(data) : data,
        }),
      );
      handleTapOver({ event, data } as EventPayload);
      switch (event) {
        case EventType.ERROR:
          if (
            data instanceof ResetAuthError &&
            data.code === AuthnErrorCode.CREDENTIAL_EXPIRED
          ) {
            displayBanner(data);
            break;
          }
          // DA: an unknown user can still log in to the desktop with raw credentials;
          // the form itself explains it, so no user-not-found banner. With the username
          // already typed in authn, only the password is asked.
          if (
            isDesktopAccess() &&
            data?.code === AuthnErrorCode.USER_NOT_FOUND
          ) {
            dispatch(
              promptDirectCredentialsAction({
                username: getAttemptedUsername(data),
              }),
            );
            break;
          }
          if (
            data instanceof AppError &&
            data.code !== AuthnErrorCode.AUTHENTICATOR_NOT_ENROLLED
          ) {
            displayBanner(data);
          }
          // authn parks the face factor on a terminal timeout screen and leaves the restart to the host
          if (data?.code === AuthnErrorCode.FACE_TIMEOUT) {
            transitionAuthn(restartAuthn);
          }
          if (
            isDesktopAccess() &&
            data?.code === AuthnErrorCode.NETWORK_ERROR
          ) {
            log(
              'Network error, showing the offline screen',
              JSON.stringify({ data }),
            );

            setOfflineFactors([
              { factorType: AuthnMethod.PROX, factorCategory: 'have' },
            ]);
            setAuthnNetworkFailed(true);

            mapErrorToAction(data.code, dispatch);
          } else if (isEpcsOrderSigning()) {
            if (
              !(data instanceof RetryableError) ||
              data?.code === AuthnErrorCode.FACTORS_INSUFFICIENT
            ) {
              const traceContext = getTraceContext();
              endJourneyAndCloseWindow(traceContext.workflowId, traceContext);
            }
          }
          // Terminal: authn holds its surface and defers terminal UI to the
          // host, so any FatalError is the host's to disposition with a
          // full-page error screen rendered in place of the authn surface.
          if (data instanceof AuthnFatalError) {
            // the error screen supersedes any banner, the store's included
            clearBanner();
            dispatch(clearError());
            transitionAuthn(current => failAuthn(current, data));
          }
          break;

        case EventType.OFFLINE:
          // DA: no stored banner. The offline screen replaces <Auth>, which then never sees ONLINE to
          // clear it; the screen states the lost connection itself.
          if (!isDesktopAccess()) {
            showOfflineBanner();
          }
          break;

        case EventType.ONLINE:
        case EventType.CLEAR_BANNER:
        case EventType.RESTART_STATE:
        case EventType.DEVICE_RECONNECTED:
        case EventType.PERMISSION_GRANTED:
          clearBanner();
          break;

        // a cancelled face flow (biometric notice "Do later", declined consent or reconsent,
        // the retry screen's Cancel) holds a loader until the host restarts authn
        case EventType.CANCEL:
          clearBanner();
          transitionAuthn(restartAuthn);
          break;

        case EventType.START_ENROLLING:
          enrollingFace.current = data === true;
          break;

        case EventType.ENROLLED:
          if (!enrollingFace.current) {
            setBanner({
              status: 'success' as NOTIF_BANNER_TYPE,
              message: t('enroll.awesome-msg'),
              clearBanner,
            });
          }
          break;

        case EventType.IMPR_ID_ERROR:
          if (data instanceof AppError) {
            displayBanner(data);
          }
          mapErrorToAction(ErrorCode.IID_AUTH_ERROR, dispatch);
          break;
        case EventType.INTERNAL_ERROR:
        case EventType.CAMERA_ERROR:
        case EventType.CAMERA_IN_USE:
        case EventType.PERMISSION_REVOKED:
          if (data) {
            displayBanner(toAppError(data));
          }
          break;
        case EventType.DEVICE_DISCONNECTED:
          if (data) {
            displayBanner(toAppError(data, AuthnErrorCode.DEVICE_DISCONNECTED));
          }
          break;
        case EventType.TERMINATE_SESSION:
          // In case of epcs order signing, the window is always closed
          if (isEpcsOrderSigning()) {
            mapErrorToAction(ErrorCode.TERMINATE_BY_TIMEOUT, dispatch);
            break;
          }
          // authn restarts its views on a timeout but keeps the stored session (an abandoned
          // face lock only emits this event), so the next tap would resume on it
          if (data === TERMINATE_REASON_CODE.TIMEOUT) {
            transitionAuthn(restartAuthn);
          }
          break;
        // authn ends the push ceremony on a loader and leaves the next screen to the host,
        // so a denial ends the journey at once.
        case EventType.PUSH_DENIED:
          mapErrorToAction(ErrorCode.PUSH_NOTIFICATION_REJECTED, dispatch);
          break;
        case EventType.AUTHENTICATED:
          dispatch(
            startApplicationAccessAction.request({
              ...contextResource,
              // TODO: Remove this assertion by fixing type in authn module
              ...data,
            }),
          );
          break;
        case EventType.FACTORS:
          dispatch(
            setAuthenticationStatusAction(
              AuthenticationReadinessStatusCode.READY,
            ),
          );
          break;
        case EventType.NOT_READY:
          dispatch(
            setAuthenticationStatusAction(
              AuthenticationReadinessStatusCode.NOT_READY,
            ),
          );
          break;
      }
    },
    [
      contextResource,
      handleTapOver,
      dispatch,
      displayBanner,
      showOfflineBanner,
      clearBanner,
      setBanner,
      transitionAuthn,
      t,
    ],
  );

  const { fatalError, banner: oauthBanner } = useGetEpicOauthFlowError();
  const banner = isEpicOauthFlow().failure
    ? oauthBanner
    : (authnBanner ?? errorBanner);

  debug(
    '[AppAccess] Error: ',
    JSON.stringify({ error }),
    'Banner: ',
    JSON.stringify(banner),
    'FatalError: ',
    JSON.stringify(fatalError),
  );
  if (isEpicOauthFlow().success && !error) {
    debug(
      '[AppAccess] Epic Oauth success and no errors detected, not displaying UI',
    );
    return <></>;
  }

  // DA offline: the agent can still log in to the desktop with raw credentials.
  // An authn network failure skips the debounce so authn's error view never flashes.
  const isDesktopOffline =
    isDesktopAccess() &&
    (!isNavigatorOnline ||
      debouncedNetworkConnectionStatus ===
        InternetConnectivityStatus.DISCONNECTED ||
      (authnNetworkFailed &&
        networkConnectionStatus === InternetConnectivityStatus.DISCONNECTED));
  const prompt =
    uiCaptureCredentials ?? (isDesktopOffline ? offlinePrompt : null);

  const credentialScreen = prompt && (
    <LearnCredentialsScreen
      credentialType={getCredentialsType()}
      prompt={prompt}
      logoUrl={orgPreferences?.logoUrl}
    />
  );

  // The learn flow is its own screen; the direct prompt is part of the auth flow.
  if (credentialScreen && prompt.kind === CredentialPromptKind.LEARN) {
    return credentialScreen;
  }

  return (
    <PortalLayout>
      {credentialScreen || (
        <div className="app-access-container">
          {(ready && initialFactors && orgPreferences !== undefined && (
            <Auth
              key={authnState.run}
              banner={banner}
              fatalError={
                authnState.kind === 'failed' ? authnState.error : undefined
              }
              factors={offlineFactors}
              deviceFactors={initialFactors}
              requestConfig={requestConfig}
              contextResource={contextResource}
              onEvent={onEvent}
              callbacks={authnCallbacks}
              orgPreferences={orgPreferences ?? undefined}
              hideFactorSwitch={
                // this check is to close factor switcher while offline mode
                // we need to consider to pass offline status to authn-module to avoid this outer checks
                debouncedNetworkConnectionStatus ===
                  InternetConnectivityStatus.DISCONNECTED ||
                !config.FACTOR_SWITCH_ENABLED
              }
            />
          )) || <></>}
        </div>
      )}
    </PortalLayout>
  );
};

export default App;
